import { orgAPI } from "@/api/api";
import { makeRoom } from "@/api/boundedMap";
import { findRowInList } from "@/api/fileRecordCache";
import {
  fileRecordListKey,
  type FileRecordListScope,
} from "@/api/fileRecordKeys";
import {
  earliestPresignExpiryMs,
  fileRecordKeys,
  fileRecordListQuery,
  useFileRecordRows,
} from "@/api/fileRecords";
import { getApiErrorStatus } from "@/api/utils";
import Sentry from "@/integrations/Sentry";
import { usePublicCanvasArtifactSafe } from "@/public-canvas/PublicCanvasArtifactContext";
import { rewritePublicCanvasHtmlUrls } from "@/public-canvas/rewritePublicCanvasHtmlUrls";
import { isRestoredQueryData } from "@/queryPersistence";
import type { FileRecord } from "@cohesive/api-client";
import {
  extractReferencedFiles,
  PUBLIC_URL_REGEX,
  reversePresignedUrlsIn,
} from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";

export interface HtmlPresignRewrite {
  /** Rewrite embedded public file URLs to presigned ones before display
   *  (one bulk round trip). Transport errors leave the HTML untouched. */
  transformHtml: (html: string) => Promise<string>;
  /**
   * Reverse of `transformHtml` for content coming BACK from a live iframe
   * (island innerHTML): swap presigned URLs to their stable public form so
   * saves never persist expiring URLs. Handles both the raw URL and its
   * `&`→`&amp;` variant (innerHTML serializes ampersands in attributes).
   */
  reversePresignedUrls: (html: string) => string;
  /**
   * Iframe asset-404 feedback (detail pane): mark the file record behind a
   * presigned URL as missing so it is never re-presigned — its original
   * public URL stays in place on the next rewrite. Returns true when newly
   * marked (caller should re-deliver the content).
   */
  markMissingByPresignedUrl: (url: string) => boolean;
  /** Ids that should no longer be presigned — folded into the pane's srcDoc
   *  fingerprint so a 404-driven re-deliver registers as new content. */
  getKnownMissingIds: () => ReadonlySet<string>;
  /** Clear all bookkeeping (detail pane record switch). */
  reset: () => void;
}

export interface UseHtmlPresignRewriteOptions {
  /** Sentry context for presign failures. */
  fileRecordId?: string;
  /** The list the document's own row is in; embedded files are usually there too. */
  scope?: FileRecordListScope;
}

/**
 * ONE presign rewrite for every HTML surface — the detail pane and the
 * canvas block card share this hook (previously two near-identical copies:
 * `rewritePublicFileUrls` in HtmlContent and `useHtmlBlockContentTransform`).
 *
 * Rewrites `https://file.{env}.cohesive.ai/public/{id}` references to the
 * presigned URL on the embedded file's row — from the document's scope list,
 * else its own `GET` — so embedded assets resolve even when unpublished.
 * Every id it resolves becomes an observed row for as long as this document
 * is mounted, so the list re-reads keep its links alive with everything
 * else — and a remount rebuilds the same document with the same URLs, which
 * the browser serves from its cache. When a link is renewed, `transformHtml`
 * takes a new identity: the document runs it again and swaps the link in
 * place. Per-id failures (record deleted, still processing, permission
 * denied) accumulate in `knownMissingIds` and keep their original public URL
 * — stable and CloudFront-cacheable, and a published copy may still serve
 * them.
 *
 * The presigned→public map ACCUMULATES across rotations (never replaced):
 * a non-remounting iframe can hold island content presigned by an earlier
 * delivery, and reversal must still find it — replacing the map per delivery
 * would let an old presigned URL slip into a save.
 */
/** A renewal cycle adds an entry per link and never replaces the old one. */
const MAX_REMEMBERED_LINKS = 500;

function remember(map: Map<string, string>, key: string, value: string) {
  if (!map.has(key)) makeRoom(map, MAX_REMEMBERED_LINKS);
  map.set(key, value);
}

function linkExpired(row: FileRecord): boolean {
  const expiresAt = earliestPresignExpiryMs(row);
  return expiresAt !== null && expiresAt <= Date.now();
}

/** Statuses that answer for one id, rather than saying the call failed. */
function isPerIdFailure(error: unknown): boolean {
  const status = getApiErrorStatus(error);
  return status === 400 || status === 403 || status === 404;
}

export function useHtmlPresignRewrite(
  options: UseHtmlPresignRewriteOptions = {},
): HtmlPresignRewrite {
  const fileRecordIdRef = useRef(options.fileRecordId);
  fileRecordIdRef.current = options.fileRecordId;

  const queryClient = useQueryClient();
  const knownMissingIdsRef = useRef<Set<string>>(new Set());
  // The embedded rows this document reads its links from. Observing them is
  // what keeps them checked and renewed; accumulates, because an island edit
  // re-transforms one region and the rest of the document still needs its
  // links.
  const [embeddedIds, setEmbeddedIds] = useState<string[]>([]);
  const { rows: embeddedRows } = useFileRecordRows(embeddedIds, options.scope);
  // The links the observed rows carry now. A renewal gives `transformHtml` a
  // new identity — which is how a mounted document learns to run it again
  // and swap the link in place.
  const linkSignature = Array.from(embeddedRows.values())
    .map((view) => view.record?.url || "")
    .join("|");
  const scopeRef = useRef(options.scope);
  scopeRef.current = options.scope;
  // Observing a row is what keeps its link renewed with its list, and what
  // gives `transformHtml` a new identity when the link changes.
  const observe = useCallback((ids: string[]) => {
    setEmbeddedIds((prev) => {
      const added = ids.filter((id) => !prev.includes(id));
      return added.length === 0 ? prev : [...prev, ...added];
    });
  }, []);
  // presigned URL → the exact public URL string it replaced.
  const presignedToPublicRef = useRef<Map<string, string>>(new Map());
  // presigned URL → file-record id (asset-404 traceback).
  const presignedToIdRef = useRef<Map<string, string>>(new Map());

  // The public share SPA has no authenticated API: reading the rows there
  // would sit on the axios auth interceptor waiting for a token getter that is
  // never installed. Rewrite against the published canvas.json files map
  // instead — no network, no auth, and it resolves the embedded media that
  // canvas publish copied under the canvas prefix. A pre-fix artifact with no
  // files map still takes this branch (rewriting nothing) rather than falling
  // through to presign.
  const publicCanvas = usePublicCanvasArtifactSafe();
  // Keyed on the narrow fields, not the context value — that churns identity
  // on every file-pane open/close. `shareOrigin` is required on the provider,
  // so it doubles as the public-view presence signal; the `?? {}` stays for
  // pre-fix artifacts whose canvas.json has no files map.
  const publicCanvasFiles = publicCanvas?.artifact.files;
  const publicCanvasShareOrigin = publicCanvas?.shareOrigin;
  const publicCanvasRewrite = useMemo(
    () =>
      publicCanvasShareOrigin != null
        ? {
            files: publicCanvasFiles ?? {},
            shareOrigin: publicCanvasShareOrigin,
          }
        : null,
    [publicCanvasFiles, publicCanvasShareOrigin],
  );

  const transformHtml = useCallback(
    async (html: string) => {
      if (publicCanvasRewrite) {
        return rewritePublicCanvasHtmlUrls(
          html,
          publicCanvasRewrite.files,
          publicCanvasRewrite.shareOrigin,
        );
      }
      const referenced = extractReferencedFiles(html);
      const idsToResolve = referenced
        .map((ref) => ref.id)
        .filter((id) => !knownMissingIdsRef.current.has(id));
      if (idsToResolve.length === 0) return html;

      // The scope list first: one read, shared with the list already mounted,
      // and a remount resolves from the rows already held, so the document it
      // builds is byte-identical to the one before it. A list restored from
      // disk has lost its links and is read again rather than asked per id.
      const scope = scopeRef.current;
      const listed = scope
        ? await queryClient
            .fetchQuery({
              ...fileRecordListQuery(scope),
              staleTime: isRestoredQueryData(
                queryClient.getQueryState(fileRecordListKey(scope))
                  ?.dataUpdatedAt ?? 0,
              )
                ? 0
                : Infinity,
            })
            .catch(() => [] as FileRecord[])
        : [];
      const rows = await Promise.all(
        idsToResolve.map(async (id) => {
          const held =
            findRowInList(listed, id) ??
            queryClient.getQueryData<FileRecord>(fileRecordKeys.detail(id));
          if (held?.url && !linkExpired(held)) {
            return { id, url: held.url, error: null as unknown };
          }
          try {
            const row = await queryClient.fetchQuery<FileRecord>({
              queryKey: fileRecordKeys.detail(id),
              queryFn: () => orgAPI.fileRecords.getById(id),
              retry: false,
            });
            return { id, url: row.url, error: null as unknown };
          } catch (error) {
            return { id, url: undefined, error };
          }
        }),
      );

      // Anything that is not an answer about one id — no network, a 429 from
      // the firewall, a 5xx — is the call failing: leave the HTML alone and
      // try again next time rather than marking every id missing for good.
      const transportFailure = rows.find(
        ({ error }) => error && !isPerIdFailure(error),
      );
      if (transportFailure) {
        Sentry.captureException(transportFailure.error, {
          tags: { feature: "html-publish-rewrite" },
          extra: {
            fileRecordId: fileRecordIdRef.current,
            referencedIdCount: referenced.length,
            stage: "embedded-file-links",
          },
        });
        // The body goes out with its public URLs, which is not an answer:
        // observe the rows anyway, so the transform runs again with a new
        // identity once they resolve.
        observe(idsToResolve);
        return html;
      }

      const replacements = new Map<string, string>();
      const unserved: string[] = [];
      for (const { id, url, error } of rows) {
        // A record the API refused stays missing. A row with no link (just
        // uploaded and not yet served, a canvas, a reference whose target is
        // gone) keeps the public URL and is observed: when its link arrives,
        // the transform changes identity and the document is rewritten.
        if (url) replacements.set(id, url);
        else if (error) knownMissingIdsRef.current.add(id);
        else unserved.push(id);
      }
      observe([...replacements.keys(), ...unserved]);
      if (replacements.size === 0) return html;

      return html.replace(PUBLIC_URL_REGEX, (original, id: string) => {
        const presigned = replacements.get(id);
        if (!presigned) return original;
        remember(presignedToPublicRef.current, presigned, original);
        remember(presignedToIdRef.current, presigned, id);
        return presigned;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [publicCanvasRewrite, queryClient, observe, linkSignature],
  );

  const reversePresignedUrls = useCallback(
    (html: string) =>
      reversePresignedUrlsIn(html, presignedToPublicRef.current),
    [],
  );

  const markMissingByPresignedUrl = useCallback((url: string) => {
    const id = presignedToIdRef.current.get(url);
    if (!id || knownMissingIdsRef.current.has(id)) return false;
    knownMissingIdsRef.current.add(id);
    return true;
  }, []);

  const getKnownMissingIds = useCallback(
    (): ReadonlySet<string> => knownMissingIdsRef.current,
    [],
  );

  const reset = useCallback(() => {
    knownMissingIdsRef.current = new Set();
    presignedToPublicRef.current = new Map();
    presignedToIdRef.current = new Map();
    setEmbeddedIds([]);
  }, []);

  return useMemo(
    () => ({
      transformHtml,
      reversePresignedUrls,
      markMissingByPresignedUrl,
      getKnownMissingIds,
      reset,
    }),
    [
      transformHtml,
      reversePresignedUrls,
      markMissingByPresignedUrl,
      getKnownMissingIds,
      reset,
    ],
  );
}
